import React from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { STATE_COLORS } from './constants';
import { BulkUploadStatus } from './types';

interface Props {
  uploads: Array<BulkUploadStatus>;
  isLoading: boolean;
  onShowResults: (upload: BulkUploadStatus) => void;
}

export default function UploadHistoryTable({ uploads, isLoading, onShowResults }: Props) {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (uploads.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Typography variant="h6" color="text.secondary" mb={1}>
          No uploads in progress
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload a file above to get started with bulk rate plan imports
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Upload ID</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Processed</TableCell>
            <TableCell>Success</TableCell>
            <TableCell>Valid Rows</TableCell>
            <TableCell>Errors</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {uploads.map((upload) => (
            <TableRow key={upload.batchId}>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{upload.batchId}</TableCell>
              <TableCell>
                <Chip label={upload.state} color={STATE_COLORS[upload.state]} size="small" />
              </TableCell>
              <TableCell>
                <Typography>{upload.totalRows}</Typography>
              </TableCell>
              <TableCell>
                <Typography>
                  {upload.processedRows} of {upload.validRowsCount}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography color="success.main">{upload.successCount}</Typography>
              </TableCell>
              <TableCell>
                <Typography>
                  {upload.validRowsCount} of {upload.totalRows}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography color={upload.errorCount > 0 ? 'error.main' : 'text.primary'}>
                  {upload.errorCount}
                </Typography>
              </TableCell>
              <TableCell>{upload.createdAt ? new Date(upload.createdAt).toLocaleString() : '-'}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  {upload.results && upload.results.length > 0 ? (
                    <Button size="small" variant="outlined" onClick={() => onShowResults(upload)}>
                      Show Results ({upload.results.length})
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
